import { useNavigate } from 'react-router-dom'
import { memo } from 'react'

/**
 * Componente de tarjeta pequeña (Small Card) para representar un ticket en listas.
 * * **Características principales**:
 * - **Accesibilidad**: Implementa `role="button"` y `tabIndex` para soporte de teclado.
 * - **Optimización**: Utiliza `React.memo` para evitar re-renderizados costosos en listas extensas.
 * - **Navegación**: Redirige al detalle del ticket mediante `react-router-dom`.
 * - **Robustez**: Incluye un guard clause para evitar errores si el objeto `ticket` es nulo.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.ticket - Objeto que contiene la información del ticket.
 * @param {number|string} props.ticket.id - ID único del ticket.
 * @param {string} props.ticket.title - Título o asunto del reporte.
 * @param {string} [props.ticket.systemOrigin] - Nombre del sistema de origen (opcional).
 * @param {'open'|'closed'|'pending'} props.ticket.status - Estado actual para definir el color del indicador.
 * * @returns {JSX.Element|null} Una fila interactiva o null si no existe información del ticket.
 */
const CardsmTicket = ({ ticket }) => {
  /** @type {function} Hook para la navegación programática */
  const navigate = useNavigate()

  // 1. Manejo de errores básico para evitar que la app explote si no hay ticket
  if (!ticket) return null

  /**
   * Ejecuta la navegación hacia la ruta de detalle del ticket.
   */
  const handleNavigation = () => {
    navigate(`/ticket/${ticket.id}`)
  }

  // 2. Mapeo de colores para el estado (opcional pero recomendado)
  /** @type {Object.<string, string>} Clases de utilidad de Bootstrap según el estado */
  const statusColors = {
    open: 'text-warning',
    closed: 'text-success',
    pending: 'text-danger',
  }

  return (
    <div
      role="button" // 3. Accesibilidad: Indica que es clickeable
      tabIndex={0} // Permite navegar con el teclado
      onClick={handleNavigation}
      onKeyDown={(e) => e.key === 'Enter' && handleNavigation()}
      className="cardsm-ticket d-flex align-items-center gap-2 p-2 rounded-2"
    >
      {/* Indicador de Estado */}
      <span
        className={`small ${statusColors[ticket.status] || 'text-secondary'}`}
        title={`Estado: ${ticket.status}`}
        style={{ fontSize: 9 }}
      >
        ●
      </span>

      {/* Contenido Principal */}
      <div className="flex-grow-1 min-w-0">
        <div className="d-flex align-items-center gap-2">
          <span className="small fw-medium text-truncate">{ticket.title}</span>
          <span className="text-body-secondary" style={{ fontSize: 11 }}>
            #{ticket.id}
          </span>
        </div>
        <small className="text-body-secondary" style={{ fontSize: 11 }}>
          {ticket.systemOrigin || 'Sin origen'}
        </small>
      </div>

      <i className="fa-solid fa-chevron-right text-body-secondary fa-xs"></i>

      <style>{`
        .cardsm-ticket {
          cursor: pointer;
          border: 1px solid var(--gm-border);
          transition: background var(--gm-transition), border-color var(--gm-transition);
        }
        .cardsm-ticket:hover {
          background: var(--gm-surface-2);
          border-color: var(--gm-primary);
        }
      `}</style>
    </div>
  )
}

// 4. Memoización para evitar re-renders innecesarios en listas largas
export default memo(CardsmTicket)
